<template>
  <component
    :is="tag"
    :class="buttonClasses"
    :disabled="disabled || loading"
    :type="tag === 'button' ? type : undefined"
    :to="tag === 'router-link' ? to : undefined"
    :href="tag === 'a' ? href : undefined"
    :aria-disabled="disabled || loading"
    :aria-label="ariaLabel"
    v-bind="$attrs"
    @click="handleClick"
    @focus="handleFocus"
    @blur="handleBlur"
  >
    <!-- Loading spinner -->
    <div
      v-if="loading"
      class="inline-flex items-center justify-center mr-2"
      aria-hidden="true"
    >
      <svg
        class="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>

    <!-- Icon (left) -->
    <component
      v-if="iconLeft && !loading"
      :is="iconLeft"
      class="mr-2 h-4 w-4"
      aria-hidden="true"
    />

    <!-- Button content -->
    <span v-if="$slots.default" class="inline-flex items-center">
      <slot />
    </span>

    <!-- Icon (right) -->
    <component
      v-if="iconRight && !loading"
      :is="iconRight"
      class="ml-2 h-4 w-4"
      aria-hidden="true"
    />
  </component>
</template>

<script setup>
import { computed, defineEmits } from 'vue'

// Props definition
const props = defineProps({
  // Button content and behavior
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary', 'secondary', 'tertiary', 
      'danger', 'success', 'warning',
      'ghost', 'link', 'outline', 'glass'
    ].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['xs', 'sm', 'small', 'md', 'medium', 'lg', 'large'].includes(value)
  },
  
  // Button states
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: false
  },
  
  // Button element type
  tag: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'a', 'router-link'].includes(value)
  },
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value)
  },
  
  // Link/router props
  to: {
    type: [String, Object],
    default: null
  },
  href: {
    type: String,
    default: null
  },
  
  // Icons
  iconLeft: {
    type: [String, Object],
    default: null
  },
  iconRight: {
    type: [String, Object],
    default: null
  },
  
  // Accessibility
  ariaLabel: {
    type: String,
    default: null
  }
})

// Events
const emit = defineEmits(['click', 'focus', 'blur'])

// Computed classes following Basket Base patterns
const buttonClasses = computed(() => {
  const classes = [
    // Base styles using design system
    'inline-flex items-center justify-center font-medium rounded-lg',
    'transition-base-design',
    'focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    'cursor-pointer',

    // Size variants (supports both Tailwind convention xs/sm/md/lg and verbose small/medium/large)
    {
      'px-2 py-1 text-xs': props.size === 'xs',
      'px-3 py-1.5 text-sm': props.size === 'small' || props.size === 'sm',
      'px-4 py-2 text-base': props.size === 'medium' || props.size === 'md',
      'px-6 py-3 text-lg': props.size === 'large' || props.size === 'lg'
    },

    // Width
    {
      'w-full': props.fullWidth,
      'w-auto': !props.fullWidth
    }
  ]

  // Variant-specific styles using design system classes and tokens
  switch (props.variant) {
    case 'primary':
      classes.push(
        'btn-primary btn-micro-interaction',
        // Override default padding with size-specific padding
        props.size === 'xs' && 'px-2 py-1',
        (props.size === 'small' || props.size === 'sm') && 'px-3 py-1.5',
        (props.size === 'medium' || props.size === 'md') && 'px-4 py-2',
        (props.size === 'large' || props.size === 'lg') && 'px-6 py-3'
      )
      break
      
    case 'secondary':
      classes.push(
        'btn-secondary',
        // Override default padding with size-specific padding
        props.size === 'xs' && 'px-2 py-1',
        (props.size === 'small' || props.size === 'sm') && 'px-3 py-1.5',
        (props.size === 'medium' || props.size === 'md') && 'px-4 py-2',
        (props.size === 'large' || props.size === 'lg') && 'px-6 py-3'
      )
      break
      
    case 'tertiary':
      classes.push(
        'bg-legal-100 text-legal-700 border border-legal-200',
        'hover:bg-legal-200 hover:border-legal-300',
        'focus-ring-design',
        'active:bg-legal-300',
        'transition-colors'
      )
      break
      
    case 'danger':
      classes.push(
        'bg-error-600 text-white border border-error-600',
        'hover:bg-error-700 hover:border-error-700',
        'focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2',
        'active:bg-error-800',
        'transition-colors'
      )
      break
      
    case 'success':
      classes.push(
        'bg-success-600 text-white border border-success-600',
        'hover:bg-success-700 hover:border-success-700',
        'focus:outline-none focus:ring-2 focus:ring-success-500 focus:ring-offset-2',
        'active:bg-success-800',
        'transition-colors'
      )
      break
      
    case 'warning':
      classes.push(
        'bg-warning-500 text-white border border-warning-500',
        'hover:bg-warning-600 hover:border-warning-600',
        'focus:outline-none focus:ring-2 focus:ring-warning-400 focus:ring-offset-2',
        'active:bg-warning-700',
        'transition-colors'
      )
      break
      
    case 'ghost':
      classes.push(
        'btn-ghost',
        // Override default padding with size-specific padding
        props.size === 'xs' && 'px-1.5 py-0.5',
        (props.size === 'small' || props.size === 'sm') && 'px-2 py-1',
        (props.size === 'medium' || props.size === 'md') && 'px-3 py-1.5',
        (props.size === 'large' || props.size === 'lg') && 'px-4 py-2'
      )
      break
      
    case 'link':
      classes.push(
        'bg-transparent text-accent-600 border border-transparent p-0',
        'hover:text-accent-primary-hover hover:underline',
        'focus-visible-design',
        'active:text-accent-800',
        'transition-colors'
      )
      break
      
    case 'outline':
      classes.push(
        'bg-white text-accent-primary border-2 border-accent-primary',
        'hover:border-accent-primary-hover hover:bg-accent-light hover:text-accent-primary-hover',
        'focus-ring-design',
        'active:bg-accent-lighter active:border-accent-primary-hover',
        'shadow-sm hover:shadow-md',
        'transition-all gpu-accelerated-hover',
        'backdrop-blur-sm'
      )
      break
      
    case 'glass':
      classes.push(
        'glass-card-light text-accent-primary-hover',
        'hover:bg-white/90 hover:border-accent-lighter hover:text-accent-primary-hover',
        'focus-ring-design',
        'active:bg-white/95',
        'shadow-md hover:shadow-lg',
        'transition-all gpu-accelerated-hover',
        'ring-1 ring-white/20'
      )
      break
  }

  return classes
})

// Event handlers
const handleClick = (event) => {
  if (props.disabled || props.loading) {
    event.preventDefault()
    return
  }
  emit('click', event)
}

const handleFocus = (event) => {
  emit('focus', event)
}

const handleBlur = (event) => {
  emit('blur', event)
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Loading spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Ensure proper alignment for icon-only buttons */
.inline-flex {
  vertical-align: middle;
}

/* Focus ring offset adjustment for better visual */
.focus\:ring-offset-2:focus {
  --tw-ring-offset-width: 2px;
}
</style>